import React from 'react';

export interface AttachmentItem {
  readonly attachmentId: string;
  readonly filename: string;
  readonly size: number;
  readonly mimeType: string;
  readonly isCover: boolean;
  readonly uploadedAt: string;
}

export interface AttachmentsProps {
  readonly attachments: AttachmentItem[];
  readonly onUpload: (file: File) => void;
  readonly onDownload: (attachmentId: string) => void;
  readonly onDelete: (attachmentId: string) => void;
  readonly onSetCover: (attachmentId: string) => void;
}

/**
 * Attachment section — file list with upload dropzone, download, delete, and cover selection.
 */
export function Attachments({
  attachments,
  onUpload,
  onDownload,
  onDelete,
  onSetCover,
}: AttachmentsProps): React.ReactElement {
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onUpload(file);
  };

  return (
    <section aria-label="Attachments" className="mb-4">
      <h3 className="text-xs font-medium text-muted-foreground uppercase mb-2">
        Attachments ({attachments.length})
      </h3>

      {/* Upload area */}
      <div className="mb-3">
        <label
          htmlFor="file-upload"
          className="block w-full border-2 border-dashed border-border rounded-lg p-4 text-center cursor-pointer hover:border-primary transition-colors"
        >
          <span className="text-sm text-muted-foreground">Drop file here or click to upload</span>
          <input
            id="file-upload"
            type="file"
            onChange={handleFileChange}
            className="sr-only"
            aria-label="Upload attachment"
          />
        </label>
      </div>

      {/* File list */}
      {attachments.length > 0 && (
        <ul className="space-y-2" role="list" aria-label="Attachment list">
          {attachments.map((att) => (
            <li key={att.attachmentId} className="flex items-center justify-between p-2 bg-muted rounded">
              <div className="flex items-center gap-2 min-w-0">
                <FileIcon mimeType={att.mimeType} />
                <div className="min-w-0">
                  <button
                    onClick={() => onDownload(att.attachmentId)}
                    className="text-sm text-primary hover:underline truncate block"
                    aria-label={`Download ${att.filename}`}
                  >
                    {att.filename}
                  </button>
                  <span className="text-xs text-muted-foreground">{formatFileSize(att.size)}</span>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {isImage(att.mimeType) && !att.isCover && (
                  <button
                    onClick={() => onSetCover(att.attachmentId)}
                    aria-label={`Set ${att.filename} as cover`}
                    className="text-xs text-muted-foreground hover:text-primary px-1"
                  >
                    Cover
                  </button>
                )}
                {att.isCover && (
                  <span className="text-xs text-primary font-medium px-1">★ Cover</span>
                )}
                <button
                  onClick={() => onDelete(att.attachmentId)}
                  aria-label={`Delete ${att.filename}`}
                  className="text-xs text-muted-foreground hover:text-destructive px-1"
                >
                  ✕
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function FileIcon({ mimeType }: { mimeType: string }): React.ReactElement {
  const icon = isImage(mimeType) ? '🖼' : '📄';
  return <span aria-hidden="true" className="text-lg">{icon}</span>;
}

function isImage(mimeType: string): boolean {
  return mimeType.startsWith('image/');
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
